<template>
  <div class="rounded-lg shadow-lg overflow-hidden font-title">
    <div class="w-full bg-grimace-50 text-center py-6">
      <div class="flex justify-center items-center px-8 xl:px-4">
        <component
          :is="headingIs || 'h2'"
          class="cursor-pointer flex-1 break-words text-neutral-700 font-semibold text-xl text-start xl:text-center xl:px-4 hover:text-grimace-800 transition"
          @click="changeTab(1)"
          @keydown="changeTab(1)"
        >
          {{ $t('blogpost.why-pet-parents-love-vetster') }}
        </component>
        <fa :icon="['fal', tabIndex === 1 ? 'chevron-down' : 'chevron-up']" />
      </div>
      <transition
        name="accordion"
        @enter="enter"
        @after-enter="afterEnter"
        @leave="leave"
      >
        <div v-show="tabIndex === 1">
          <div class="flex xl:block items-center px-8 xl:px-6 pt-8 xl:pt-0">
            <fa
              :icon="['fal', 'video']"
              class="text-4xl xl:text-5xl xl:pt-8 fa-fw"
            />
            <component
              :is="headingIs || 'h3'"
              class="break-words text-neutral-700 xl:pt-2 text-start xl:text-center pl-4 xl:pl-0"
            >
              {{ $t('blogpost.book-online-appointments') }}
            </component>
          </div>
          <div class="flex xl:block items-center px-8 xl:px-6 pt-8 xl:pt-0">
            <fa
              :icon="['fal', 'location-dot']"
              class="text-4xl xl:text-5xl xl:pt-8 fa-fw"
            />
            <component
              :is="headingIs || 'h3'"
              class="break-words text-neutral-700 xl:pt-2 text-start xl:text-center pl-4 xl:pl-0"
            >
              {{ $t('blogpost.access-expert-pet-care') }}
            </component>
          </div>
          <div class="flex xl:block items-center px-8 xl:px-6 pt-8 xl:pt-0">
            <fa
              :icon="['fal', 'user-doctor']"
              class="text-4xl xl:text-5xl xl:pt-8 fa-fw"
            />
            <component
              :is="headingIs || 'h3'"
              class="break-words text-neutral-700 xl:pt-2 text-start xl:text-center pl-4 xl:pl-0"
            >
              {{ $t('blogpost.choose-from-licensed-veterinarians') }}
            </component>
          </div>
        </div>
      </transition>
    </div>
    <div class="w-full border-t border-zinc-200 bg-neutral-50 text-center py-6">
      <div class="flex justify-center items-center px-8 xl:px-4">
        <component
          :is="headingIs || 'h3'"
          class="cursor-pointer flex-1 break-words text-neutral-700 font-semibold text-xl text-start xl:text-center xl:px-4 hover:text-grimace-800 transition"
          @click="changeTab(2)"
          @keydown="changeTab(2)"
        >
          {{ $t('blogpost.meet-our-vets') }}
        </component>
        <fa :icon="['fal', tabIndex === 2 ? 'chevron-down' : 'chevron-up']" />
      </div>
      <transition
        name="accordion"
        @enter="enter"
        @after-enter="afterEnter"
        @leave="leave"
      >
        <div
          v-show="tabIndex === 2"
          class="px-8 xl:px-4"
        >
          <div
            v-if="partners && partners.length"
            class="grid grid-flow-col gap-4 xl:block overflow-auto scrollbar-none"
          >
            <div
              v-for="partner in partners"
              :key="partner.uuid"
              class="mt-4 min-w-[216px] xl:min-w-0"
            >
              <a
                :href="partnerUrl(partner)"
                class="block cursor-pointer group h-full"
              >
                <div class="border rounded-lg pt-4 bg-white h-full flex flex-col justify-between">
                  <div
                    v-lazy-container="{ selector: 'img' }"
                    class="flex justify-start px-4"
                  >
                    <img
                      v-if="partner.uri_photo"
                      class="w-12 h-12 rounded-full"
                      :data-src="partner.uri_photo"
                      :alt="partner.first_name"
                    />
                    <fa
                      v-else
                      class="fa-fw"
                      :icon="['fal', 'user-md']"
                    />
                    <div class="pl-2 text-start">
                      <div class="font-semibold text-neutral-600">{{ fullName(partner) }}</div>
                      <div class="vetster-small text-neutral-600">{{ fullDesignation(partner) }}</div>
                    </div>
                  </div>
                  <div class="mt-4 flex flex-col justify-start items-start">
                    <div
                      v-if="partner.rating_average"
                      class="flex whitespace-nowrap px-4 items-center w-full text-sm space-x-1"
                    >
                      <span>
                        <fa
                          :icon="['fad', 'star']"
                          class="fa-fw text-bubblegum"
                        />
                        <span class="font-bold text-pink-700">
                          {{ formatRating(partner) }}
                        </span>
                      </span>
                      <span class="text-neutral-600">
                        ({{ partner.rating_count }} {{ $tc('quote.review', partner.rating_count) }})
                      </span>
                    </div>
                    <div class="flex justify-end w-full mt-4">
                      <div class="w-auto flex items-center sm:items-baseline sm:space-x-2 justify-end text-sm sm:leading-normal rounded-tl-md text-grimace-600 pr-1 pl-2 lg:pr-2 py-2 bg-grimace-100">
                        <span>{{ $t('app.from') }}</span>
                        <span class="text-lg font-bold flex items-center leading-tight sm:leading-normal -mt-1 sm:mt-0">
                          {{
                            $n(formatMoneyRounded(partner.rate), {
                              style: 'currency',
                              currency: partner.rate_currency,
                              maximumFractionDigits: 0,
                              minimumFractionDigits: 0,
                            })
                          }}
                          <fa
                            :icon="['fal', 'angle-right']"
                            class="text-xl fa-fw"
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </a>
            </div>
            <div>
              <vetster-button-legacy
                class="flex justify-center w-full mt-4"
                variant="outline"
                :href="guidedSearchUrl"
              >
                {{ $t('search.see-more') }}
              </vetster-button-legacy>
            </div>
          </div>
          <div v-else>
            <div class="col-span-1 md:col-span-3">
              <div class="vetster-small text-left mt-4">
                {{ $t('search.we-have-thousands') }}
              </div>
              <vetster-button
                v-cloak
                type="a"
                class="w-full flex items-center justify-center mt-6"
                variant="primary"
                :href="guidedSearchUrl"
                rel="nofollow"
              >
                <fa
                  class="fa-fw mr-2 text-xl"
                  :icon="['fal', 'search']"
                />
                <span>{{ $t('symptoms-and-conditions.book-an-online-vet') }}</span>
              </vetster-button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon';
import { mapActions, mapGetters } from 'vuex';

import Designation from '@/enums/Designation';
import {
  formatMoneyRounded,
  formatRating,
} from '@/helpers/format';
import { getUrl } from '@/helpers/url';
import { isMarketSiteCountry } from '@/i18n';
import LocalizesRoutes from '@/mixins/LocalizesRoutes';
import User from '@/models/User';

export default {
  name: 'VetsterSideCollapse',

  mixins: [LocalizesRoutes],

  props: {
    headingIs: {
      type: String,
      default: '',
    },
    language: {
      type: String,
      default: 'en',
    },
    speciesTypes: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      tabIndex: 1,
      partners: [],
      searchLocation: {
        city: null,
        province: null,
        country: null,
      },
    };
  },

  computed: {
    ...mapGetters('geolocation', ['getGeolocation']),
  },

  async mounted() {
    await this.setSearchLocation();
    await this.fetchPartners();
  },

  methods: {
    ...mapActions('geolocation', ['fetchGeolocation']),
    afterEnter(el) {
      // eslint-disable-next-line no-param-reassign
      el.style.height = 'auto';
    },
    changeTab(tabIndex) {
      this.tabIndex = tabIndex;
    },
    enter(el) {
      // eslint-disable-next-line no-param-reassign
      el.style.height = 'auto';
      const { height } = getComputedStyle(el);
      // eslint-disable-next-line no-param-reassign
      el.style.height = 0;
      getComputedStyle(el);
      setTimeout(() => {
        // eslint-disable-next-line no-param-reassign
        el.style.height = height;
      });
    },
    formatMoneyRounded,
    formatRating,
    async setSearchLocation() {
      await this.fetchGeolocation();

      if (isMarketSiteCountry(this.getGeolocation.country?.toLowerCase())) {
        this.searchLocation = this.getGeolocation;
      }
    },
    async fetchPartners() {
      const speciesTreated = this.speciesTypes?.map((type) => type.value);
      const payload = {
        date_from: DateTime.local().startOf('day').toISODate(),
        time_from: '08:00',
        time_to: '20:00',
        designation: Designation.Veterinarian,
        timezone: DateTime.local().zoneName,
        country: this.searchLocation.country,
        province: this.searchLocation.province,
        city: this.searchLocation.city,
        language: this.language,
        platform: 'WEB',
        // currency: this.getSiteCurrency,
        species_treated: speciesTreated,
        areas_of_interest: [],
        page: 1,
        per_page: 3,
      };
      await this.$api.post('p/search', payload).then((response) => {
        this.partners = response.data.data.map((item) => new User(item));
        setTimeout(() => {
          this.loading = false;
        }, 500);
      });
    },
    fullName(partner) {
      return partner?.fullNameWithTitle();
    },
    fullDesignation(partner) {
      return partner?.fullDesignation();
    },
    leave(el) {
      // eslint-disable-next-line no-param-reassign
      el.style.height = getComputedStyle(el).height;
      getComputedStyle(el);
      setTimeout(() => {
        // eslint-disable-next-line no-param-reassign
        el.style.height = 0;
      });
    },
    partnerUrl(partner) {
      return getUrl(`${partner.locale.toLowerCase()}/p/${partner.uri_slug}`);
    },
  },
};
</script>

<style scoped>
.accordion-enter-active,
.accordion-leave-active {
  transition: height 250ms ease-out;
  overflow: hidden;
}
</style>
